import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Heart, MessageCircle, Bell, Plus, User, Menu, ShieldCheck, LogOut, Package, ArrowLeft } from "lucide-react";

function Wordmark() {
  const navigate = useNavigate();
  const goHome = () => {
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <button type="button" onClick={goHome} data-testid="logo-home" className="flex items-baseline gap-1 font-display">
      <span className="text-xl font-extrabold tracking-tight text-brand">Venda</span>
      <span className="text-lg font-bold text-price">Market</span>
    </button>
  );
}

const navItems = [
  { to: "/favorites", label: "Saved", icon: Heart, auth: true },
  { to: "/my-listings", label: "My Listings", icon: Package, auth: true },
  { to: "/messages", label: "Messages", icon: MessageCircle, auth: true },
];

export function Navbar() {
  const { user, guest, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ["notif-summary"],
    queryFn: () => apiFetch("/notifications/summary"),
    enabled: !!user,
    refetchInterval: 20000,
  });
  const unreadMsg = summary?.messages_total || 0;
  const unreadOther = summary?.total || 0;

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        {location.pathname !== "/" && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="nav-back"
            aria-label="Go back"
            className="-ml-1 flex h-9 w-9 items-center justify-center rounded-full text-foreground transition hover:bg-secondary"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <Wordmark />

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            if (item.auth && !user) return null;
            const Icon = item.icon;
            const showBadge = item.to === "/messages" && unreadMsg > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${
                    isActive ? "bg-brand text-white" : "text-foreground hover:bg-secondary"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
                {showBadge && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-price px-1 text-[10px] font-bold text-white">
                    {unreadMsg}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="nav-sell-btn"
            onClick={() => navigate(user ? "/create" : "/login")}
            className="hidden items-center gap-1.5 rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-400 hover:shadow-md sm:flex"
          >
            <Plus className="h-4 w-4" /> Sell
          </button>

          {user && (
            <Link to="/notices" data-testid="nav-notices" className="relative rounded-full p-2 text-foreground hover:bg-secondary">
              <Bell className="h-5 w-5" />
              {unreadOther > 0 && (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-price px-1 text-[10px] font-bold text-white">
                  {unreadOther}
                </span>
              )}
            </Link>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-trigger" className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-bold text-white">
                  {user.avatar_url ? (
                    <img src={resolveImage(user.avatar_url)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    initials(user.name)
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-1">
                  {user.name}
                  {user.verified && <ShieldCheck className="h-3.5 w-3.5 text-sky-600" />}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/edit-profile")}>Edit profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/support")}>Support</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/notification-settings")}>Notification settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/privacy")}>Privacy policy</DropdownMenuItem>
                {user.is_admin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="font-semibold text-brand">
                      Admin panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={() => navigate("/login")}
              className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-light"
            >
              Sign in
            </button>
          )}

          <button className="rounded-full p-2 hover:bg-secondary md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-border bg-white px-4 py-2 md:hidden">
          {navItems.map((item) => {
            if (item.auth && !user) return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium ${isActive ? "bg-brand text-white" : "text-foreground"}`
                }
              >
                <Icon className="h-4 w-4" /> {item.label}
              </NavLink>
            );
          })}
          <button
            onClick={() => { setOpen(false); navigate(user ? "/create" : "/login"); }}
            className="mt-1 flex w-full items-center gap-2 rounded-lg bg-sky-500 px-3 py-2.5 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" /> Sell an item
          </button>
        </nav>
      )}
    </header>
  );
}
